import type { TrackerCopy } from './types'

/** Conteúdo do tracker em português (mercado brasileiro). */
export const TRACKER_COPY_PT: TrackerCopy = {

  meta: {
    title: 'Onde Está o Papai Noel? Mapa e Contagem Regressiva para o Natal',
    description:
      'Descubra quanto falta para o Natal, por onde o Papai Noel está passando e quando ele poderá chegar à sua região.',
    keywords: [
      'onde esta o papai noel',
      'quanto falta para o natal',
      'quanto falta para o papai noel',
      'por onde esta o papai noel',
      'que horas o papai noel chega',
      'rota do papai noel',
      'contagem regressiva natal',
      'quantos dias faltam para o natal',
      'santa tracker',
      'mapa do papai noel',
      'rastrear papai noel',
    ],
    schemaName: 'Mapa e contagem regressiva do Papai Noel',
  },


  hero: {
    badge: {
      preparing: 'Oficina do Polo Norte',
      liftoff: 'Últimos preparativos',
      traveling: 'Entrega em andamento',
      completed: 'Missão cumprida',
    },
    h1: {
      preparing: 'Quanto falta para o Papai Noel chegar?',
      liftoff: 'O Papai Noel está prestes a decolar',
      traveling: 'Onde está o Papai Noel agora?',
      completed: 'O Papai Noel já voltou para o Polo Norte',
    },
    intro: {
      preparing:
        'No Polo Norte ninguém para: os duendes embrulham presentes, as renas treinam e a grande lista é conferida de novo e de novo. Aqui você vê quanto falta para o Natal e como andam os preparativos.',
      liftoff:
        'Faltam poucas horas para a viagem mais longa do ano. O trenó já está carregado, o Rudolph está com o nariz aceso e o Polo Norte não prega o olho.',
      traveling:
        'É noite de Natal e o trenó está dando a volta ao mundo. Acompanhe a rota no mapa, veja por onde ele acabou de passar e descubra quando deve chegar à sua região.',
      completed:
        'Todos os presentes foram entregues e as renas estão descansando no Polo Norte. O próximo Natal já começou a ser preparado: aqui está a contagem regressiva.',
    },
    toMap: 'Ver o mapa',
  },

  countdown: {
    heading: {
      preparing: 'O Papai Noel chega à sua região em',
      liftoff: 'O trenó decola em',
      traveling: 'Ele chega à sua região em',
      completed: 'Para o próximo Natal faltam',
    },
    units: { days: 'dias', hours: 'horas', minutes: 'minutos', seconds: 'segundos' },
    christmasNote: 'O Natal é dia 25 de dezembro: faltam {days} dias.',
    christmasToday: 'Hoje é Natal!',
    arrivingNow: 'Ele está sobrevoando a sua região!',
    ariaLabel: 'Contagem regressiva para a chegada do Papai Noel',
  },

  status: {
    preparing: {
      far: [
        'Os duendes estão fazendo os primeiros brinquedos do ano.',
        'O Papai Noel estreou um caderno novo para a grande lista.',
        'A oficina está testando ideias de presentes para o próximo Natal.',
        'As renas estão voltando aos poucos das férias.',
      ],
      months: [
        'A oficina do Polo Norte trabalha sem parar.',
        'O Papai Noel está conferindo a grande lista.',
        'As renas começaram os treinos.',
        'Estão chegando as primeiras cartas do mundo inteiro.',
      ],
      weeks: [
        'A Mamãe Noel está preparando biscoitos para a oficina toda.',
        'O Papai Noel está conferindo a lista pela segunda vez.',
        'Os duendes estão embrulhando presentes a toda velocidade.',
        'O trenó está na oficina, pronto para a revisão.',
      ],
      days: [
        'Falta pouquinho para o trenó decolar!',
        'Os duendes estão fechando os últimos sacos.',
        'O Rudolph está treinando para a grande noite.',
        'No Polo Norte já ninguém dorme.',
      ],
      hours: [
        'O Papai Noel está carregando os últimos presentes.',
        'O trenó está pronto para decolar.',
        'As renas já estão atreladas, esperando o sinal.',
        'O Rudolph já está com o nariz aceso.',
      ],
    },
    liftoff: [
      'O Papai Noel está carregando os últimos presentes.',
      'O Rudolph já está com o nariz aceso.',
      'Os duendes estão fechando o grande saco.',
      'O trenó está pronto para decolar.',
    ],
    traveling: [
      'O trenó acabou de passar por {city}.',
      'O Papai Noel deixa {city} com o saco um pouco mais leve.',
      'O Rudolph guia o trenó depois da parada em {city}.',
      'Em {city} os presentes já chegaram.',
    ],
    completed: [
      'O Papai Noel terminou o percurso.',
      'Todos os presentes foram entregues.',
      'As renas já estão descansando no Polo Norte.',
      'O próximo Natal já está sendo preparado.',
    ],
    lastVisited: 'Último lugar visitado',
    nextStop: 'Próximo destino',
    flyingTo: 'Voando rumo a {leg}',
    atNorthPole: 'No Polo Norte',
    backHome: 'De volta em casa',
    legLabel: {
      'north-pole': 'o Polo Norte',
      pacific: 'o Pacífico',
      oceania: 'a Oceania',
      'east-asia': 'a Ásia Oriental',
      'southeast-asia': 'o Sudeste Asiático',
      'central-asia': 'a Ásia Central e do Sul',
      'middle-east': 'o Oriente Médio',
      'eastern-europe': 'a Europa Oriental',
      africa: 'a África',
      'western-europe': 'a Europa Ocidental',
      'south-america': 'a América do Sul',
      'north-america': 'a América do Norte',
      'north-pole-return': 'o Polo Norte',
    },
  },

  map: {
    heading: 'A rota do Papai Noel',
    subheading: 'Toque no trenó ou em qualquer cidade para saber mais.',
    ariaLabel: 'Mapa-múndi com a rota do Papai Noel',
    sleighLabel: 'Trenó do Papai Noel',
    northPoleLabel: 'Polo Norte',
    legend: { visited: 'Já passou', current: 'Está aqui', upcoming: 'Ainda não' },
    stopStatus: {
      visited: 'O Papai Noel já passou por aqui',
      current: 'O Papai Noel está aqui agora',
      upcoming: 'O Papai Noel ainda não chegou',
    },
    localTime: 'Hora local: {time}',
    close: 'Fechar',
    followSleigh: 'Seguir o trenó',
    wholeWorld: 'Ver o mundo inteiro',
    listHeading: 'A rota, passo a passo',
    listIntro:
      'A mesma viagem em forma de lista, caso você prefira ler ou o mapa fique pequeno na sua tela.',
    parentsNote:
      'Aviso para pais e mães: esta é uma experiência natalina simulada. A rota é calculada a partir dos fusos horários; não é uma localização real nem rastreamento por GPS.',
  },

  eta: {
    heading: 'A que horas ele chega na sua casa?',
    body: 'O Papai Noel deve chegar à sua região aproximadamente entre {start} e {end}.',
    zone: 'Calculado para o seu fuso horário ({zone}).',
    remaining: 'Faltam {countdown}.',
    passed: 'O Papai Noel já passou pela sua região. Corre ver embaixo da árvore!',
    unknownZone: 'Calculado pelo relógio do seu aparelho.',
    disclaimer:
      'É uma faixa aproximada, não um horário garantido: o Papai Noel prefere passar quando todo mundo já está dormindo.',
  },

  stats: {
    heading: {
      preparing: 'Agora mesmo na oficina',
      liftoff: 'Tudo pronto para a grande noite',
      traveling: 'A viagem em números',
      completed: 'Como foi a viagem',
    },
    labels: {
      giftsReady: 'Presentes prontos',
      giftsDelivered: 'Presentes entregues',
      lettersReceived: 'Cartas recebidas',
      elvesWorking: 'Duendes trabalhando',
      reindeerTraining: 'Renas treinando',
      kilometers: 'Quilômetros',
      citiesVisited: 'Cidades visitadas',
      countriesVisited: 'Países visitados',
      homesWaiting: 'Casas esperando',
      travelMinutes: 'Tempo de viagem',
    },
    units: { kilometers: 'km', hours: 'h' },
  },

  workshop: {
    heading: 'Agora mesmo no Polo Norte',
    activities: {
      preparing: [
        'Os duendes estão embrulhando presentes.',
        'O Papai Noel está lendo cartas.',
        'As renas estão treinando na pista de decolagem.',
        'A Mamãe Noel está fazendo biscoitos.',
        'O trenó está passando pela revisão anual.',
        'Na sala da grande lista os nomes estão sendo conferidos.',
      ],
      liftoff: [
        'O trenó está passando pela última revisão.',
        'O Rudolph está treinando para a grande noite.',
        'Os duendes estão carregando os sacos no trenó.',
        'A Mamãe Noel está preparando chocolate quente para a viagem.',
      ],
      traveling: [
        'Os duendes acompanham a viagem da torre de controle.',
        'A Mamãe Noel deixa a lareira acesa esperando a volta.',
        'Na oficina já estão recolhendo os últimos papéis de presente.',
        'As renas reservas estão de prontidão, por via das dúvidas.',
      ],
      completed: [
        'As renas descansam no estábulo do Polo Norte.',
        'A Mamãe Noel serve o chocolate quente pós-viagem.',
        'Os duendes arrumam a oficina para o ano que vem.',
        'O Papai Noel guarda a grande lista até o próximo Natal.',
      ],
    },
    night: [
      'É noite no Polo Norte: a oficina trabalha com a luz baixa.',
      'Os duendes do turno da noite embrulham em silêncio.',
      'O Papai Noel dorme um pouco antes do grande dia.',
      'Só dá para ouvir a neve estalando em volta da oficina.',
    ],
  },

  surprises: {
    northPole: 'Você acordou a aurora boreal! Os duendes estão acenando da oficina.',
    celebration: 'Percurso concluído! Feliz Natal.',
  },

  controls: {
    soundOn: 'Ativar som',
    soundOff: 'Silenciar',
    fullscreenEnter: 'Tela cheia',
    fullscreenExit: 'Sair da tela cheia',
    share: 'Compartilhar',
    shareCopied: 'Link copiado',
    motionOn: 'Ativar animações',
    motionOff: 'Reduzir movimento',
  },

  share: {
    title: 'Onde está o Papai Noel?',
    text: {
      preparing: 'Descubra quanto falta para o Papai Noel chegar.',
      liftoff: 'O Papai Noel está prestes a decolar!',
      traveling: 'Veja por onde o Papai Noel está passando!',
      completed: 'O Papai Noel terminou o percurso. Feliz Natal!',
    },
  },

  faq: {
    heading: 'Perguntas frequentes',
    items: [
      {
        question: 'Quanto falta para o Natal?',
        answer:
          'A contagem regressiva desta página mostra até os segundos. O Natal é dia 25 de dezembro, e o Papai Noel sai do Polo Norte na noite do dia 24.',
      },
      {
        question: 'Onde está o Papai Noel agora?',
        answer:
          'Depende do dia. Na maior parte do ano ele está no Polo Norte preparando o Natal, e na noite de Natal dá a volta ao mundo. O mapa desta página mostra onde ele deve estar a cada momento.',
      },
      {
        question: 'Por onde o Papai Noel está passando?',
        answer:
          'Ele começa pelo Pacífico, segue pela Oceania e pela Ásia, atravessa o Oriente Médio, a Europa e a África, desce para a América do Sul e termina na América do Norte antes de voltar para casa. Ele persegue a noite: entrega onde já escureceu.',
      },
      {
        question: 'Quando o Papai Noel sai do Polo Norte?',
        answer:
          'Ele decola na tarde do dia 24 de dezembro, quando nas ilhas do Pacífico já é quase meia-noite. A partir daí não para até a manhã do dia 25.',
      },
      {
        question: 'Que horas o Papai Noel chega?',
        answer:
          'Em cada casa ele chega quando as crianças já estão dormindo, mais ou menos entre nove da noite e meia-noite. Nesta página você vê a faixa aproximada calculada para o seu fuso horário.',
      },
      {
        question: 'O Papai Noel passa por todos os países?',
        answer:
          'Sim: os 195 países do mundo entram na rota. No mapa aparecem algumas cidades como referência, mas o trenó passa por toda parte.',
      },
      {
        question: 'Como ele consegue dar a volta ao mundo em uma noite só?',
        answer:
          'Porque ele voa para o oeste, perseguindo a noite: indo no mesmo sentido em que a Terra gira, a noite de Natal dele dura mais de trinta horas. Isso, e um pouco de magia do Polo Norte.',
      },
      {
        question: 'E se eu estiver acordado quando ele chegar?',
        answer:
          'O Papai Noel é discreto: se percebe que alguém está acordado, segue viagem e volta mais tarde. O melhor jeito de ele passar cedo é ir dormir cedo.',
      },
      {
        question: 'Como o Papai Noel sabe onde eu moro?',
        answer:
          'Pelas cartas. Cada carta que chega ao Polo Norte é anotada na grande lista com o nome e a cidade, e os duendes montam a rota com essas informações.',
      },
      {
        question: 'Quando ele volta para o Polo Norte?',
        answer:
          'Na manhã do dia 25 de dezembro, depois da última entrega. As renas descansam alguns dias e a oficina já começa a pensar no Natal seguinte.',
      },
      {
        question: 'O mapa mostra a localização real dele?',
        answer:
          'Não. É uma experiência natalina simulada: a rota é calculada a partir dos fusos horários para combinar com a noite de cada lugar. Não há rastreamento por GPS nem localização real, e esta página nunca pede a sua localização.',
      },
      {
        question: 'Como posso falar com o Papai Noel?',
        answer:
          'No PapaNoel.chat você pode preparar uma chamada em que o Papai Noel fala diretamente com o seu filho ou a sua filha, pelo nome e em tempo real. Você decide o que ele sabe de antemão.',
      },
      {
        question: 'Posso fazer uma videochamada com o Papai Noel?',
        answer:
          'Pode. Além da chamada de voz, dá para fazer uma videochamada personalizada em que o Papai Noel conhece o nome, os gostos, as conquistas e os desejos do seu filho ou da sua filha.',
      },
    ],
  },

  cta: {
    heading: 'Enquanto você espera, pode falar com o Papai Noel',
    body: 'Prepare uma videochamada personalizada em que o Papai Noel vai conhecer o nome, os gostos, as conquistas e os desejos do seu filho ou da sua filha.',
    primary: 'Falar com o Papai Noel',
    secondary: 'Descobrir personagens mágicos',
    free: 'Querem experimentar antes? Fale de graça com o Papai Noel',
    travelingNote:
      'O Papai Noel está bem ocupado esta noite, mas você ainda pode preparar uma videochamada mágica.',
  },

  stops: {
    'north-pole': 'Polo Norte',
    'north-pole-return': 'Polo Norte',
    auckland: 'Auckland',
    suva: 'Suva',
    sydney: 'Sydney',
    melbourne: 'Melbourne',
    brisbane: 'Brisbane',
    'port-moresby': 'Port Moresby',
    darwin: 'Darwin',
    tokyo: 'Tóquio',
    seoul: 'Seul',
    shanghai: 'Xangai',
    beijing: 'Pequim',
    'hong-kong': 'Hong Kong',
    manila: 'Manila',
    singapore: 'Singapura',
    jakarta: 'Jacarta',
    bangkok: 'Bangkok',
    dhaka: 'Daca',
    kathmandu: 'Katmandu',
    'new-delhi': 'Nova Délhi',
    karachi: 'Karachi',
    kabul: 'Cabul',
    dubai: 'Dubai',
    tehran: 'Teerã',
    riyadh: 'Riade',
    istanbul: 'Istambul',
    moscow: 'Moscou',
    athens: 'Atenas',
    warsaw: 'Varsóvia',
    cairo: 'Cairo',
    johannesburg: 'Joanesburgo',
    lagos: 'Lagos',
    berlin: 'Berlim',
    rome: 'Roma',
    paris: 'Paris',
    madrid: 'Madri',
    london: 'Londres',
    lisbon: 'Lisboa',
    reykjavik: 'Reiquiavique',
    'rio-de-janeiro': 'Rio de Janeiro',
    'sao-paulo': 'São Paulo',
    'buenos-aires': 'Buenos Aires',
    santiago: 'Santiago',
    'la-paz': 'La Paz',
    caracas: 'Caracas',
    bogota: 'Bogotá',
    lima: 'Lima',
    'new-york': 'Nova York',
    toronto: 'Toronto',
    havana: 'Havana',
    'mexico-city': 'Cidade do México',
    chicago: 'Chicago',
    denver: 'Denver',
    'los-angeles': 'Los Angeles',
    vancouver: 'Vancouver',
    anchorage: 'Anchorage',
    honolulu: 'Honolulu',
  },
}
